import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import styled from 'styled-components';
import { AnimatedStepBody } from '../animation/StepBodyAnimations';
import { useIsFramedCentredStep } from '../hooks/useIsFramedCentredStep';
import { getPostAccountCreationSteps } from '../store/flow/selectors';
import { devices } from 'signup-constants/styles/viewports.styles';
import { GreetingsPretitle } from '../views-components/GreetingsPretitle';
import { StepSubtitle } from '../views-components/StepSubtitle';
import { StepTitle } from '../views-components/StepTitle';
import { Step } from 'signup-constants/Step';
import { LayoutTemplateBelowTitleContainer, LayoutTemplateContentContainer } from '../app/AppViewComponent.styles';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { useIndustry } from '../step-industry/useIndustry';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { getFlowConfig } from '../flow-initial/getFlowConfig';
import MessagingCopy from '../messaging/MessagingCopy';
import { getStepConfig } from './getStepConfig';
import Honeypots from '../honeypots/CustomInput';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BaseViewHeader = styled.header.withConfig({
  displayName: "BaseView__BaseViewHeader",
  componentId: "sc-3btd5l-0"
})(["margin-bottom:1.5rem;", " @media ", "{", " ", "}"], props => !props.isPostAccountCreationStep && `text-align: center;`, devices.desktop, props => props.isFramedCentredStep && props.stepKey === Step.HubletSelection && `margin-bottom: 0;`, props => props.isFramedCentredStep && props.stepKey === Step.JobRole && `margin-left: 20px;`);
const BaseViewHelperText = styled.div.withConfig({
  displayName: "BaseView__BaseViewHelperText",
  componentId: "sc-3btd5l-1"
})(["", " ", " ", ""], props => props.isFramedCentredStep && ![Step.CrmExperienceLevel].includes(props.stepKey) && `text-align: left;`, props => !props.isPostAccountCreationStep && `text-align: center;`, props => props.stepKey === Step.HubletSelection && `text-align: left !important; `);
export const BaseView = ({
  title,
  helperText,
  footer,
  children,
  stepKey,
  navMarker,
  preTitleMessage = '',
  addExperimentClassName = false,
  disableAnimation = false,
  expandableSubTitleSection = null
}) => {
  const {
    currentStep
  } = useCurrentStep();
  const step = stepKey || currentStep;
  const {
    isFramedCentredStep
  } = useIsFramedCentredStep();
  const {
    layoutType
  } = useLayoutTemplate();
  const stepConfig = getStepConfig(step);
  const {
    clickedToSeeAll,
    predictedIndustries
  } = useIndustry();
  const predictions = predictedIndustries.length > 0;
  const postAccountCreation = useSelector(getPostAccountCreationSteps);
  const {
    isAssignedToVariant: isACQ0149Variant
  } = useExperiment(ExperimentKeys.ACQ0149, TrackWhen.never);
  const {
    isAssignedToVariant: isCOAC002Variant
  } = useExperiment(ExperimentKeys.COAC0002, TrackWhen.never);
  if (!step) {
    return null;
  }
  const helperTextMessage = isACQ0149Variant() && [Step.CompanyName, Step.CompanyDomain].includes(step) ? /*#__PURE__*/_jsx(MessagingCopy, {
    message: `views.${step}.acq0149HelperTextExperience`
  }) : helperText;
  const shouldRestrictWidth = stepConfig && stepConfig.shouldRestrictWidth || false;
  const isPostAccountCreationStep = postAccountCreation.includes(currentStep);
  const shouldUseWave = getFlowConfig().shouldUseWaveOnPostAccountCreation;
  const stepTitle = /*#__PURE__*/_jsx(StepTitle, {
    isFramedCentredStep: isFramedCentredStep,
    stepKey: step
  });
  const navMarkerName = navMarker || `${step}_VIEW`;
  const experimentSeleniumAttribute = addExperimentClassName ? {
    'data-selenium-experiment': 'EXPERIMENT'
  } : null;
  const getPretitle = () => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(GreetingsPretitle, {
        hasPreTitle: isValidI18nKey(preTitleMessage)
      }), /*#__PURE__*/_jsx(StepSubtitle, {
        message: preTitleMessage
      })]
    });
  };
  return /*#__PURE__*/_jsxs(AnimatedStepBody, {
    dataStep: step,
    disableAnimation: disableAnimation,
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: navMarkerName
    }), /*#__PURE__*/_jsxs("div", Object.assign({
      "data-selenium": `${step}_VIEW`
    }, experimentSeleniumAttribute, {
      children: [/*#__PURE__*/_jsxs(BaseViewHeader
      // @ts-expect-error Expression produces a union type that is too complex to represent.
      , {
        isFramedCentredStep: isFramedCentredStep,
        isPostAccountCreationStep: isPostAccountCreationStep && shouldUseWave && !isCOAC002Variant(),
        stepKey: step,
        children: [getPretitle(), title || stepTitle, /*#__PURE__*/_jsxs(LayoutTemplateBelowTitleContainer, {
          isPostAccountCreationStep: isPostAccountCreationStep && shouldUseWave,
          shouldRestrictWidth: shouldRestrictWidth,
          children: [expandableSubTitleSection, /*#__PURE__*/_jsx(BaseViewHelperText, {
            isFramedCentredStep: isFramedCentredStep,
            isPostAccountCreationStep: isPostAccountCreationStep && shouldUseWave,
            stepKey: step,
            children: helperTextMessage
          })]
        })]
      }), /*#__PURE__*/_jsxs(LayoutTemplateContentContainer, {
        clickedToSeeAll: clickedToSeeAll || predictions,
        layoutType: layoutType,
        shouldRestrictWidth: shouldRestrictWidth,
        stepKey: step,
        children: [children, footer]
      })]
    })), /*#__PURE__*/_jsx(Honeypots, {})]
  });
};